import * as React from 'react';
import { IStore } from '../../redux/reducers';
import { useSelector } from 'react-redux';
import { Button, Box, HStack, useToast } from "native-base";
import { useEffect } from 'react';
import MaterialIconByName from '../MaterialIconByName';
import { downloadReportPdf, downloadScannedImage } from 'src/services/download';
import { getText } from 'src/utils/i18n';
import ReportFormatSelectionModal, { ReportFormat } from '../modal/ReportFormatSelectionModal';
import { printThermalReport58mm } from 'src/services/print/thermal';
import { IReportRejectSelectionConfig } from 'src/models/Report';
import { isLightboxFeatureEnabled } from 'src/utils/grainFeatureUtils';
import { normalizeRejectSelectionSessionConfigForReport } from 'src/utils/rejectConfig';

interface DownloadResultButtonsProps {
    rejectionCalculationConfig?: IReportRejectSelectionConfig;
    rejectionMapping?: Record<string, string>;
}

export default function DownloadResultButtons({ rejectionCalculationConfig, rejectionMapping }: DownloadResultButtonsProps) {

    const [showSCUserView, setShowSCUserView] = React.useState(false);
    const [isFormatModalOpen, setIsFormatModalOpen] = React.useState(false);
    const toast = useToast();

    const userGroups = useSelector((store: IStore) => store.user.userProfile.response?.groups);
    const lightboxEnabled = useSelector((store: IStore) =>
        isLightboxFeatureEnabled(store.user.userProfile.response?.settings)
    );

    useEffect(() => {
        if (userGroups && userGroups.includes("scAppUser")) {
            setShowSCUserView(true)
        }
    }, [userGroups]);

    const job = useSelector((store: IStore) => store.job.jobStatus.job);
    const locale = useSelector((store: IStore) => store.app.locale);
    const reportRejectionMapping = rejectionMapping || job?.additionalAttributes?.rejectionMappings;

    const [downloadingImage, setDownloadingImage] = React.useState(false);
    const [fetchingReport, setFetchingReport] = React.useState(false);

    const getReportRejectionCalculationConfig = () => {
        const config = rejectionCalculationConfig
            || (job?.results?.rejectionCalculationConfig as IReportRejectSelectionConfig | undefined);
        if (!reportRejectionMapping) {
            return config;
        }

        return normalizeRejectSelectionSessionConfigForReport(config as any, {
            grainId: String(job?.grainId || ''),
            rejectionMapping: reportRejectionMapping,
        } as any);
    };

    const downloadScannedImagesForJob = async () => {
        const startedAt = Date.now();
        setDownloadingImage(true)
        try {
            await downloadScannedImage(job)
        } catch (error) {
            toast.show({
                title: getText('downloadFailed'),
                description: getText('downloadImageFailed'),
                duration: 5000,
            });
        } finally {
            // Keep the same visible feedback as report downloads even when
            // Android's Download Manager accepts the request immediately.
            const remaining = 400 - (Date.now() - startedAt);
            if (remaining > 0) {
                await new Promise(resolve => setTimeout(resolve, remaining));
            }
            setDownloadingImage(false)
        }
    }

    const handleReportDownloadClick = () => {
        setIsFormatModalOpen(true);
    }

    const downloadReportForJob = async (format: ReportFormat) => {
        setFetchingReport(true)
        try {
            const reportRejectionCalculationConfig = getReportRejectionCalculationConfig();
            if (format === 'thermal_58mm') {
                await printThermalReport58mm(job, locale, reportRejectionCalculationConfig);
                setIsFormatModalOpen(false);
                toast.show({
                    title: getText('printStarted'),
                    description: getText('thermalPrintStarted'),
                    duration: 3000,
                });
                return;
            }

            await downloadReportPdf(job, format, locale, reportRejectionCalculationConfig)
            setIsFormatModalOpen(false);
            toast.show({
                title: getText('downloadSuccessful'),
                description: getText('reportDownloadSuccess', { format: format.toUpperCase() }),
                duration: 3000,
            });
        } catch (error) {
            toast.show({
                title: format === 'thermal_58mm' ? getText('printFailed') : getText('downloadFailed'),
                description: format === 'thermal_58mm'
                    ? getText('printReportFailed')
                    : getText('downloadReportFailed'),
                duration: 5000,
            });
        } finally {
            setFetchingReport(false)
        }
    }

    return (
        <Box>
            {!showSCUserView && <HStack
                style={{
                    justifyContent: 'center',
                    marginBottom: lightboxEnabled ? 12 : 10,
                    marginTop: lightboxEnabled ? 12 : 10,
                    ...(lightboxEnabled ? { flexWrap: 'wrap' as const } : {}),
                }}
                {...(lightboxEnabled ? { flexDirection: "row", alignItems: "center" } : {})}
                space={2}>
                {
                    ((job?.input_object_key !== undefined && job?.input_object_key !== "")
                        || (job?.processingMode === 'local' && (job?.results?.scanned_images?.length || 0) > 0)) &&
                    <Button
                        startIcon={<MaterialIconByName name="download" />}
                        isLoading={downloadingImage}
                        isLoadingText={getText('downloading')}
                        onPress={downloadScannedImagesForJob}>
                        {getText('scannedImages')}
                    </Button>
                }
                <Button
                    isLoading={fetchingReport}
                    startIcon={<MaterialIconByName name="download" />}
                    isLoadingText={getText('generatingReport')}
                    onPress={handleReportDownloadClick}>
                    {getText('report')}
                </Button>
            </HStack>}
            <ReportFormatSelectionModal
                isOpen={isFormatModalOpen}
                onClose={() => setIsFormatModalOpen(false)}
                onSelectFormat={downloadReportForJob}
                isLoading={fetchingReport}
            />
        </Box>
    );
}
